import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../Header';
import { ProcedureIcon, getProcedureType, getProcedureIconAppearance } from '../components/ProcedureIcon';
import { API_BASE_URL } from '../constants';
import { clearPractitionerSelection } from '../bookingPractitioners';
import { t, type TranslationKey } from '../i18n/translations';
import { header5DefinitionJSON } from './StartPage';
import { header5NewUserJSON } from './StartPage';
import './ProceduresPage.css';

interface Procedure {
  id: string;
  name: string;
  duration: number;
  [ruleMap: string]: any;
}

const newPatientProcedureIds = ["proc911", "proc711", "proc918", "proc914", "proc915", "proc916", "proc919", "proc917", "proc935", "proc713"];
const oldPatientProcedureIds = ["proc813", "proc911", "proc712", "proc918", "proc914", "proc915", "proc916", "proc919", "proc917", "proc935"];

const newPatientProcedureTitles: Record<string, string> = {
  proc711: 'Routinecheck',
  proc713: 'Kinderuntersuch bis 14 Jahre',
};

const procedureCacheKey = 'procedureCache';

const readStoredPatientId = () => {
  const raw = localStorage.getItem('loginPatientId');

  console.log("loginPatientId=" + raw);

  if (!raw) return '';
  return raw;
};

const readProcedureCache = () => {
  try {
    const raw = localStorage.getItem(procedureCacheKey);
    if (!raw) return {} as Record<string, Procedure>;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : ({} as Record<string, Procedure>);
  } catch (cacheError) {
    console.error('Failed to read procedure cache', cacheError);
    return {} as Record<string, Procedure>;
  }
};

const writeProcedureCache = (cache: Record<string, Procedure>) => {
  try {
    localStorage.setItem(procedureCacheKey, JSON.stringify(cache));
  } catch (cacheError) {
    console.error('Failed to write procedure cache', cacheError);
  }
};

const formatDuration = (duration?: number) => {
  if (!duration || Number.isNaN(duration)) return '';
  return `${duration} min`;
};

const procedureDisplayOrder: Record<string, number> = {
  emergency: 0,
  'exam-regular-patient': 1,
  'exam-new-patient': 1,
  'dental-cleaning-DH-60': 2,
  'cons-aligner': 3,
  'cons-impl': 4,
  'cons-bleaching': 5,
  'cons-splint': 6,
  'cons-aesth': 7,
  'cons-8': 8,
  'exam-child': 9,
  'second-opinion': 10,
  'online-appointment': 11,
};

const compareProcedureDisplayOrder = (first: Procedure, second: Procedure) =>
  (procedureDisplayOrder[getProcedureType(first.id, first.name)] ?? Number.MAX_SAFE_INTEGER) -
  (procedureDisplayOrder[getProcedureType(second.id, second.name)] ?? Number.MAX_SAFE_INTEGER);

const translateProcedureName = (procedureName: string | undefined, procedureId: string) => {
  switch (getProcedureType(procedureId, procedureName)) {
    case 'cons-aesth':
      return 'Ästhetische Zahnmedizin';
    case 'dental-cleaning-DH-60':
      return 'Zahnreinigung';
    case 'cons-splint':
      return 'Besprechung Schienen';
  }
  if (!procedureName) return 'Behandlung';
  const translatedName = t(procedureName as TranslationKey) || procedureName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const translateProcedureDescription = (procedureName: string | undefined, procedureId: string) => {
  if (getProcedureType(procedureId, procedureName) === 'cons-splint') {
    return 'Beratung zu Knirschschienen oder Schnarchschienen zur Entlastung des Kiefers';
  }
  if (!procedureName) return '';
  return t(`description-${procedureName}` as TranslationKey) || '';
};

export function ProceduresPage() {
  const hasLoginPatientId = (readStoredPatientId() !== '');
  if (hasLoginPatientId) {
    localStorage.setItem('headerDefinition', header5DefinitionJSON);
  } else {
    localStorage.setItem('headerDefinition', header5NewUserJSON);
  }

  localStorage.setItem('headerPhase', '1');

  const navigate = useNavigate();
  //const hasLoginPatientId = Boolean(readStoredPatientId());
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    localStorage.removeItem('pendingAppointmentFull');
    localStorage.setItem('selectedPainRegion', '');
    localStorage.setItem('selectedPainProblems', JSON.stringify([]));
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchProcedures = async () => {
      const procedureCache = readProcedureCache();
      const procedureIds = hasLoginPatientId ? oldPatientProcedureIds : newPatientProcedureIds;
      //const procedureIds = readStoredPatientId() ? oldPatientProcedureIds : newPatientProcedureIds;

      console.log(hasLoginPatientId + " procedureIds=" + JSON.stringify(procedureIds));

      const resolveProcedure = async (procedureId: string) => {
        const cachedProcedure = procedureCache[procedureId];
        if (cachedProcedure?.id && cachedProcedure?.name) {
          return cachedProcedure;
        }

        try {
          const response = await fetch(`${API_BASE_URL}/procedures/${procedureId}`);

          if (!response.ok) {
            console.error(`Bad procedure response for ${procedureId}: ${response.status}`);
            return undefined;
          }

          const procedure = (await response.json()) as Procedure;

          console.log("procedure=" + JSON.stringify(procedure));

          if (procedure?.id && procedure?.name) {
            procedureCache[procedureId] = procedure;
            writeProcedureCache(procedureCache);
            return procedure;
          }
        } catch (fetchError) {
          console.error(`Failed to load procedure ${procedureId}`, fetchError);
        }

        return undefined;
      };

      try {
        const resolvedProcedures = await Promise.all(
          procedureIds.map((procedureId) => resolveProcedure(procedureId))
        );

        if (!isMounted) return;

        setProcedures(
          resolvedProcedures.filter(
            (procedure): procedure is Procedure => Boolean(procedure?.id && procedure?.name)
          ).sort(compareProcedureDisplayOrder)
        );
        setError('');
      } catch (loadError) {
        console.error('Failed to load procedures', loadError);
        if (isMounted) {
          setError('Behandlungen konnten nicht geladen werden.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProcedures();

    return () => {
      isMounted = false;
    };
  }, []);

  const select = (procedureId: string) => {
    if (localStorage.getItem('selectedProcedureId') !== procedureId) {
      clearPractitionerSelection();
    }
    localStorage.setItem('selectedProcedureId', procedureId);
    localStorage.setItem('selectedAppointmentId', '');
    console.log(procedureId + ' selected');
    localStorage.setItem('previousPage', '/procedures');

    console.log("select: selectedProcedureId=" + JSON.stringify(localStorage.getItem('selectedProcedureId')));

    navigate(procedureId === 'proc911' ? '/emergency' : '/practitioners');
  };

  return (
    <div className="booking-procedures min-h-screen bg-[#faf7f2] text-slate-800">
      <Header compact />
      <div className="booking-procedures__content mx-auto max-w-[1240px] px-6 py-10 sm:px-8 lg:px-10 lg:py-12">
        <header className="mx-auto max-w-3xl text-center">
          <h1 className="text-2xl font-semibold tracking-[-0.03em] text-slate-800">
            Welche Behandlung wünschen Sie?
          </h1>
          <p className="mt-3 text-[15px] leading-7 text-[#7c8593]">
            Wählen Sie den gewünschten Behandlungsgrund aus, um fortzufahren.
          </p>
          {!hasLoginPatientId ? (
            <div className="booking-procedures__new-patient-notice">
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <circle cx="10" cy="10" r="10" fill="currentColor" />
                <circle cx="10" cy="5.5" r="1" fill="#fff" />
                <path d="M10 9v6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <p>
                Bitte beachten Sie: Der erste Termin bei Neupatienten erfolgt immer zuerst bei einer
                Zahnärztin oder einem Zahnarzt, um unseren Qualitätsstandard zu sichern. Termine für
                die Zahnreinigung vergeben wir erst danach.
              </p>
            </div>
          ) : null}
        </header>

        <div className="booking-procedures__tiles mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {loading ? <div className="text-sm text-slate-500">Behandlungen werden geladen...</div> : null}
          {!loading && error ? <div className="text-sm text-red-600">{error}</div> : null}
          {procedures.map((procedure) => (
            <button
              key={procedure.id}
              data-procedure-id={procedure.id}
              type="button"
              onClick={() => select(procedure.id)}
              className="booking-procedures__tile group flex min-h-[262px] min-w-0 flex-col rounded-[16px] border border-[#f1f2f4] bg-white p-6 text-left sm:p-[31px] transition hover:-translate-y-0.5 hover:border-[#d9c39b] hover:shadow-[0_22px_44px_rgba(15,23,42,0.06)]"
            >
              <div className="flex items-start justify-between">
                <span
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${getProcedureIconAppearance(procedure.id, procedure.name).className}`}
                >
                  <ProcedureIcon procedureId={procedure.id} procedureName={procedure.name} />
                </span>
                <span className="inline-flex min-w-[56px] items-center justify-center rounded-[4px] bg-[#f7f8fa] px-2 py-1 text-[11px] font-bold leading-4 text-[#7c8593]">
                  {formatDuration(procedure.duration)}
                </span>
              </div>

              <h3 className="mt-8 text-[20px] font-bold leading-[26px] tracking-[-0.03em] text-[#1b2230]">
                {(!hasLoginPatientId && newPatientProcedureTitles[procedure.id]) || translateProcedureName(procedure.name, procedure.id)}
              </h3>
              <p className="mt-5 text-[15px] leading-[1.5] text-[#747c89]">{translateProcedureDescription(procedure.name, procedure.id)}</p>

              <span className="mt-auto inline-flex items-center gap-2 pt-4 text-[14px] font-semibold leading-5 text-[#cbb58a]">
                Auswählen
                <span className="text-sm leading-none transition group-hover:translate-x-0.5">→</span>
              </span>
            </button>
          ))}
        </div>

        <div className="mt-14 rounded-[24px] border border-[#f1ebe1] bg-white px-6 py-6 shadow-[0_16px_36px_rgba(15,23,42,0.03)]">
          <div className="flex items-start gap-4">
            <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#fbf8f1] text-[#c7ae84]">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="9" />
                <path d="M11 11h1v6m-1 0h2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="7.5" r="1" fill="currentColor" stroke="none" />
              </svg>
            </span>
            <div className="text-sm leading-6 text-slate-400">
              <p className="font-semibold text-slate-800">Nicht sicher, was Sie brauchen?</p>
              <p>
                Rufen Sie uns gerne direkt an unter <span className="text-[#bea06a]">052 625 46 25</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
